(function () {
  'use strict';

  if (customElements.get('goldclub-patch-upsell')) return;

  class GoldclubPatchUpsell extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.form = document.getElementById(this.dataset.productFormId);
      this.toggle = this.querySelector('[data-patch-toggle]');
      this.panel = this.querySelector('[data-patch-panel]');
      this.status = this.querySelector('[data-patch-status]');
      this.dialog = this.querySelector('[data-patch-dialog]');
      this.dialogCard = this.querySelector('[data-patch-dialog-card]');
      this.dialogName = this.querySelector('[data-patch-dialog-name]');
      this.groupProperty = this.querySelector('[data-patch-group-property]');
      this.requirePlacement = this.dataset.requirePlacement === 'true';
      this.activeCheckbox = null;
      this.lastFocusedElement = null;

      this.onToggleClick = this.handleToggleClick.bind(this);
      this.onComponentChange = this.handleComponentChange.bind(this);
      this.onDialogClick = this.handleDialogClick.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);

      if (this.toggle) this.toggle.addEventListener('click', this.onToggleClick);
      this.addEventListener('change', this.onComponentChange);
      if (this.dialog) this.dialog.addEventListener('click', this.onDialogClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      this.updateProperties();
    }

    disconnectedCallback() {
      if (this.toggle) this.toggle.removeEventListener('click', this.onToggleClick);
      this.removeEventListener('change', this.onComponentChange);
      if (this.dialog) this.dialog.removeEventListener('click', this.onDialogClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.initialized = false;
    }

    get checkboxes() {
      return Array.from(this.querySelectorAll('[data-patch-checkbox]'));
    }

    get selectedCheckboxes() {
      return this.checkboxes.filter((checkbox) => checkbox.checked && !checkbox.disabled);
    }

    handleToggleClick() {
      if (!this.toggle || !this.panel) return;

      const willOpen = this.toggle.getAttribute('aria-expanded') !== 'true';
      this.toggle.setAttribute('aria-expanded', String(willOpen));
      this.panel.hidden = !willOpen;
    }

    handleComponentChange(event) {
      const checkbox = event.target.closest('[data-patch-checkbox]');
      if (!checkbox || !this.contains(checkbox)) return;

      if (!checkbox.checked) {
        delete checkbox.dataset.patchPlacement;
        this.updateOptionPlacement(checkbox);
        this.updateProperties();
        return;
      }

      if (this.requirePlacement && this.dialog) {
        this.openDialog(checkbox);
      } else {
        this.updateProperties();
      }
    }

    handleDialogClick(event) {
      const placementButton = event.target.closest('[data-patch-placement]');
      if (placementButton && this.dialog.contains(placementButton)) {
        this.confirmPlacement(placementButton.dataset.patchPlacement || '');
        return;
      }

      if (event.target.closest('[data-patch-dialog-cancel]')) {
        this.closeDialog(true);
      }
    }

    handleDocumentKeydown(event) {
      if (!this.dialog || this.dialog.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog(true);
        return;
      }

      if (event.key !== 'Tab' || !this.dialogCard) return;

      const focusable = Array.from(
        this.dialogCard.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((element) => element.offsetParent !== null);

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openDialog(checkbox) {
      if (!this.dialog) return;

      this.activeCheckbox = checkbox;
      this.lastFocusedElement = document.activeElement;
      if (this.dialogName) this.dialogName.textContent = checkbox.dataset.patchTitle || '';

      this.dialog.hidden = false;
      document.documentElement.classList.add('patch-dialog-open');
      window.requestAnimationFrame(() => {
        const firstChoice = this.dialog.querySelector('[data-patch-placement]');
        (firstChoice || this.dialogCard)?.focus();
      });
    }

    confirmPlacement(placement) {
      if (!this.activeCheckbox || !placement) return;
      this.activeCheckbox.dataset.patchPlacement = placement;
      this.updateOptionPlacement(this.activeCheckbox);
      this.closeDialog(false);
    }

    closeDialog(cancelSelection) {
      if (!this.dialog) return;

      if (cancelSelection && this.activeCheckbox) {
        this.activeCheckbox.checked = false;
        delete this.activeCheckbox.dataset.patchPlacement;
        this.updateOptionPlacement(this.activeCheckbox);
      }

      this.dialog.hidden = true;
      document.documentElement.classList.remove('patch-dialog-open');
      this.activeCheckbox = null;
      this.updateProperties();

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
      this.lastFocusedElement = null;
    }

    updateOptionPlacement(checkbox) {
      const option = checkbox.closest('.patch-upsell__option');
      const label = option ? option.querySelector('[data-patch-placement-label]') : null;
      if (!label) return;

      const placement = checkbox.dataset.patchPlacement || '';
      label.textContent = placement ? `Aplicação: ${placement}` : '';
      label.hidden = !placement;
    }

    selectedPatches() {
      return this.selectedCheckboxes.map((checkbox) => ({
        variantId: checkbox.dataset.variantId,
        title: checkbox.dataset.patchTitle || 'Patch',
        placement: checkbox.dataset.patchPlacement || ''
      }));
    }

    updateProperties() {
      const patches = this.selectedPatches();
      const propertyInputs = Array.from(this.querySelectorAll('[data-patch-property]'));

      propertyInputs.forEach((input, index) => {
        const patch = patches[index];
        input.value = patch ? `${patch.title}${patch.placement ? ` (${patch.placement})` : ''}` : '';
        input.disabled = !patch;
      });

      // Compatibility with the hidden field in older installed snippets.
      if (this.groupProperty) {
        this.groupProperty.value = '';
        this.groupProperty.disabled = true;
      }

      if (this.status) {
        this.status.dataset.state = '';
        this.status.textContent = patches.length === 0
          ? ''
          : `${patches.length} ${patches.length === 1 ? 'patch selecionado' : 'patches selecionados'}`;
      }
    }

    showError(message) {
      if (!this.status) return;
      this.status.dataset.state = 'error';
      this.status.textContent = message || this.dataset.errorMessage;
      this.status.focus?.();
    }

    resetSelection() {
      this.checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
        delete checkbox.dataset.patchPlacement;
        this.updateOptionPlacement(checkbox);
      });

      if (this.groupProperty) {
        this.groupProperty.value = '';
        this.groupProperty.disabled = true;
      }
      this.updateProperties();
    }

  }

  customElements.define('goldclub-patch-upsell', GoldclubPatchUpsell);

  function readProperties(entries) {
    const properties = {};

    entries.forEach((value, key) => {
      const match = key.match(/^properties\[(.+)]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value);
    });

    return properties;
  }

  function normalizeCartAddBody(body, headers) {
    const contentType = headers.get('content-type') || '';

    if (typeof body === 'string' && contentType.includes('application/json')) {
      const parsed = JSON.parse(body);

      if (Array.isArray(parsed.items)) {
        return { payload: parsed, items: parsed.items };
      }

      if (!parsed.id) return null;

      const item = {
        id: String(parsed.id),
        quantity: Math.max(1, Number.parseInt(parsed.quantity || '1', 10) || 1)
      };
      if (parsed.properties) item.properties = parsed.properties;
      if (parsed.selling_plan) item.selling_plan = parsed.selling_plan;

      const payload = {};
      if (parsed.sections) payload.sections = parsed.sections;
      if (parsed.sections_url) payload.sections_url = parsed.sections_url;
      payload.items = [item];
      return { payload, items: payload.items };
    }

    let entries;
    if (body instanceof FormData) {
      entries = body;
    } else if (typeof body === 'string' || body instanceof URLSearchParams) {
      entries = new URLSearchParams(body);
    } else {
      return null;
    }

    const id = entries.get('id') || entries.get('id[]');
    if (!id) return null;

    const item = {
      id: String(id),
      quantity: Math.max(1, Number.parseInt(entries.get('quantity') || '1', 10) || 1)
    };
    const properties = readProperties(entries);
    if (Object.keys(properties).length) item.properties = properties;

    const sellingPlan = entries.get('selling_plan');
    if (sellingPlan) item.selling_plan = sellingPlan;

    const payload = { items: [item] };
    const sections = entries.get('sections');
    const sectionsUrl = entries.get('sections_url');
    if (sections) payload.sections = sections;
    if (sectionsUrl) payload.sections_url = sectionsUrl;

    return { payload, items: payload.items };
  }

  function selectedComponentForItems(items) {
    const components = Array.from(document.querySelectorAll('goldclub-patch-upsell'))
      .filter((component) => component.selectedCheckboxes.length > 0);

    if (components.length === 0) return null;

    for (const component of components) {
      const formVariantId = component.form
        ? new FormData(component.form).get('id') || component.form.querySelector('[name="id"]')?.value
        : '';
      const item = items.find((candidate) => String(candidate.id) === String(formVariantId));
      if (item) return { component, item };
    }

    return components.length === 1 && items.length === 1
      ? { component: components[0], item: items[0] }
      : null;
  }

  if (!window.__goldclubPatchFetchInstalled) {
    window.__goldclubPatchFetchInstalled = true;
    const originalFetch = window.fetch.bind(window);

    window.fetch = function (resource, init) {
      const url = typeof resource === 'string' ? resource : resource && resource.url;
      const method = init && init.method ? String(init.method).toUpperCase() : 'GET';

      if (!url || !/\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(url) || method !== 'POST' || !init) {
        return originalFetch(resource, init);
      }

      try {
        const headers = new Headers(init.headers || {});
        const normalized = normalizeCartAddBody(init.body, headers);
        if (!normalized) return originalFetch(resource, init);

        const match = selectedComponentForItems(normalized.items);
        if (!match) return originalFetch(resource, init);

        const { component, item: mainItem } = match;
        const patches = component.selectedPatches();
        if (patches.length === 0) return originalFetch(resource, init);

        const quantity = Math.max(1, Number.parseInt(mainItem.quantity || '1', 10) || 1);
        mainItem.properties = Object.assign({}, mainItem.properties || {});
        // Remove only metadata introduced by this integration, not personalization.
        delete mainItem.properties._patch;
        delete mainItem.properties._patch_group;
        delete mainItem.properties._produto_principal;

        patches.forEach((patch, index) => {
          mainItem.properties[`Patch ${index + 1}`] = `${patch.title}${patch.placement ? ` (${patch.placement})` : ''}`;
        });

        const patchItems = patches.map((patch) => ({
          id: String(patch.variantId),
          quantity,
          properties: {
            Aplicação: patch.placement || 'Não informada'
          }
        }));

        normalized.payload.items = [...normalized.items, ...patchItems];
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');

        const request = originalFetch(resource, Object.assign({}, init, {
          body: JSON.stringify(normalized.payload),
          headers
        }));

        return request.then((response) => {
          if (response.ok) component.resetSelection();
          return response;
        });
      } catch (_error) {
        return originalFetch(resource, init);
      }
    };
  }
})();
